import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Copyright © 2025-2026 Cognizant Digital Business, Evolutionary AI.
 * All Rights Reserved.
 * Issued under the Academic Public License.
 */
// END COPYRIGHT
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Snackbar from "@mui/material/Snackbar";
import { styled, ThemeProvider } from "@mui/material/styles";
import { useEffect, useState } from "react";
import ExamplesSelection from "./components/ExamplesSelection";
import { GoogleAnalytics } from "./components/GoogleAnalytics";
import IdeasChart from "./components/IdeasChart";
import IdeasTable from "./components/IdeasTable";
import LandingPage from "./components/LandingPage";
import LoadingSpinner from "./components/LoadingSpinner";
import Logs from "./components/Logs";
import Navbar from "./components/Navbar";
import Scoreboard from "./components/Scoreboard";
import Sidebar from "./components/Sidebar";
import { useAppState } from "./hooks/useAppState";
import { useRuntimeConfig } from "./hooks/useRuntimeConfig";
import theme from "./theme";
import { IdeaSource } from "./types";
// #region: Styled Components
const AppContainer = styled(Box) `
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif;
    background-color: ${theme.palette.background.default};
`;
const ContentSection = styled("section") `
    background: white;
    margin-bottom: 24px;
    padding: 24px;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;
const FullHeightSection = styled(ContentSection) `
    height: calc(100vh - 96px);
    margin-bottom: 0;
    display: flex;
    flex-direction: column;
    overflow: auto;
`;
const MainContent = styled(Box, { shouldForwardProp: (prop) => prop !== "$sidebarOpen" })(({ $sidebarOpen }) => ({
    marginLeft: $sidebarOpen ? 320 : 0,
    padding: 20,
    paddingTop: "calc(56px + 20px)",
    overflowY: "auto",
    minHeight: "100vh",
}));
// #endregion: Styled Components
const App = () => {
    const appContext = useAppState();
    const [prefill, setPrefill] = useState(null);
    const [activeTab, setActiveTab] = useState("dashboard");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showLanding, setShowLanding] = useState(true);
    const [ownIdeaSession, setOwnIdeaSession] = useState(false);
    const [machineMode, setMachineMode] = useState(true);
    const { enableGoogleAnalytics, gaMeasurementId } = useRuntimeConfig();
    const toggleSidebar = () => setSidebarOpen((s) => !s);
    // The sidebar holds the topic form, so bringing your own idea means opening it straight away.
    const startFromScratch = () => {
        setShowLanding(false);
        setSidebarOpen(true);
    };
    const browseExamples = () => {
        setShowLanding(false);
        setSidebarOpen(false);
    };
    // With no session there is nothing to tear down, so this just returns to the splash.
    const startNewSession = () => {
        if (appContext.sessionId) {
            window.location.reload();
            return;
        }
        setShowLanding(true);
        setSidebarOpen(false);
    };
    // The sidebar renders the ideation controls, so a closed one would swallow the ask.
    const requestIdeation = (source) => {
        setMachineMode(source === IdeaSource.AI);
        setSidebarOpen(true);
    };
    // Reset active tab when session starts/ends and open sidebar on session start
    useEffect(() => {
        setActiveTab(appContext.sessionId ? "dashboard" : "");
        if (appContext.sessionId) {
            setSidebarOpen(true);
            // Someone who wrote their own topic came to add ideas, not to generate them.
            setMachineMode(!ownIdeaSession);
        }
    }, [appContext.sessionId]);
    const isLanding = !appContext.sessionId && showLanding;
    const handleCloseError = () => {
        appContext.clearError();
    };
    return (_jsxs(_Fragment, { children: [enableGoogleAnalytics && gaMeasurementId && _jsx(GoogleAnalytics, { gaMeasurementID: gaMeasurementId }), _jsxs(ThemeProvider, { theme: theme, children: [_jsx(CssBaseline, {}), _jsxs(AppContainer, { children: [_jsx(Navbar, { hasSession: Boolean(appContext.sessionId), active: activeTab, onNavigate: (tab) => setActiveTab(tab), isLanding: isLanding, onNewSession: startNewSession, ideasCount: appContext.appState?.ideas?.length ?? 0, onDownloadIdeas: () => appContext.downloadIdeas(), onToggleSidebar: toggleSidebar }), _jsx(Sidebar, { ...appContext, initializeSession: async (data) => {
                                    setOwnIdeaSession(true);
                                    await appContext.initializeSession(data);
                                }, prefillValues: prefill, onClearPrefill: () => setPrefill(null), isOpen: sidebarOpen, machineMode: machineMode, onModeChange: setMachineMode }), _jsxs(MainContent, { "$sidebarOpen": sidebarOpen, children: [_jsx(Snackbar, { anchorOrigin: { vertical: "top", horizontal: "right" }, onClose: handleCloseError, open: Boolean(appContext.error), children: _jsx(Alert, { onClose: handleCloseError, severity: "error", sx: { width: "100%" }, variant: "filled", children: appContext.error }) }), appContext.loading && _jsx(LoadingSpinner, {}), isLanding && (_jsx(LandingPage, { onBrowseExamples: browseExamples, onStartFromScratch: startFromScratch })), !appContext.sessionId && !showLanding && (_jsx(ContentSection, { children: _jsx(ExamplesSelection, { onPrefill: (data) => setPrefill(data), onStartSession: (data) => {
                                                setOwnIdeaSession(false);
                                                appContext.initializeSession(data);
                                            } }) })), appContext.sessionId && (_jsxs(_Fragment, { children: [activeTab === "dashboard" && (_jsxs(ContentSection, { children: [_jsx(Scoreboard, { data: appContext.scoreboardData, onRequestIdea: requestIdeation, onGenerateIdea: () => appContext.generateAIIdea(), isRequestInProgress: appContext.loading }), _jsx(IdeasChart, { data: appContext.ideasChartData }), _jsx(IdeasTable, { data: appContext.ideasChartData })] })), activeTab === "logs" && (_jsx(FullHeightSection, { children: _jsx(Logs, { appState: appContext.appState }) }))] }))] })] })] })] }));
};
export default App;
